import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

// Interface para os aparelhos/dispositivos do parceiro (da tabela devices)
interface PartnerDevice {
    id: string;
    name: string;
    code: string; // Código de ativação do dispositivo
    status: string; // active, inactive
    screen_id: string | null;
    screen_name?: string; // Nome da tela associada (se houver)
    organization_id: string;
}

// Interface para configuração de slots por dispositivo
interface DeviceSlotConfig {
    device_id: string;
    device_name: string;
    device_code: string;
    slots_per_device: number;  // Slots configurados para este dispositivo
    available_slots: number;   // Slots disponíveis para anúncios externos
}

// Interface que corresponde à tabela venue_partners do Supabase
interface VenuePartnerDB {
    id: string;
    user_id: string | null;
    // Dados do Proprietário
    owner_name: string | null;
    owner_document: string | null;
    owner_email: string | null;
    owner_phone: string | null;
    pix_key: string | null;
    // Dados do Estabelecimento
    venue_name: string;
    venue_type: string;
    venue_cep: string;
    venue_street: string;
    venue_number: string;
    venue_complement: string | null;
    venue_neighborhood: string;
    venue_city: string;
    venue_state: string;
    venue_photos: string[];
    operating_hours: string | null;
    // Capacidade e Público
    screen_quantity: number;
    daily_footfall: number | null;
    average_retention: number | null;
    audience_profile: string | null;
    // Dados Técnicos
    screen_positions: string[] | null;
    screen_orientation: string | null;
    screen_resolution: string | null;
    connection_type: string | null;
    // Slots
    available_slots: number | null;
    total_slots: number | null;
    // Outros
    tags: string[] | null;
    latitude: string | null;
    longitude: string | null;
    // Regras de Exibição
    blacklisted_categories: string[];
    custom_blacklist_brands: string[];
    status: 'pending' | 'approved' | 'rejected' | 'active' | 'inactive';
    created_at: string;
    updated_at: string;
    internal_media_cycle?: number;
    external_media_cycle?: number;
}

// Interface adaptada para exibição no componente (mantendo compatibilidade com a UI existente)
interface PointHubPartner {
    id: string;
    userId: string | null; // ID do usuário parceiro para buscar suas telas
    // Dados do Parceiro (Owner) - Mapeados do banco
    ownerName: string;
    document: string;
    email: string;
    phone: string;
    pixKey: string;
    contractStatus: 'active' | 'pending' | 'suspended';
    // Dados do Estabelecimento (Venue)
    venueName: string;
    category: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    latitude: string;
    longitude: string;
    audienceProfile: string;
    monthlyFlow: string;
    operatingHours: string;
    // Dados Técnicos (Inventory)
    screenCount: number;
    screenPositions: string[];
    screenOrientation: 'vertical' | 'horizontal' | 'both';
    screenResolution: string;
    connectionType: string;

    // Dados para Match
    photos: string[];
    tags: string[];
    availableSlots: number;
    totalSlots: number;
    createdAt: string;

    // Campos adicionais do banco
    venueNeighborhood?: string;
    venueNumber?: string;
    venueComplement?: string;
    dailyFootfall?: number | null;
    averageRetention?: number | null;
    blacklistedCategories?: string[];
    customBlacklistBrands?: string[];
    activeDeviceCount?: number; // Contagem real de dispositivos ativos
    internalMediaCycle: number;
    externalMediaCycle: number;
}

// Categorias de estabelecimento
const VENUE_CATEGORIES = [
    'Academia', 'Supermercado', 'Farmácia', 'Coworking', 'Clínica Médica',
    'Restaurante', 'Cafeteria', 'Shopping', 'Salão de Beleza', 'Pet Shop',
    'Loja de Roupas', 'Oficina Mecânica', 'Posto de Combustível', 'Hotel', 'Outro'
];

// Mapeamento de venue_type para categoria
const VENUE_TYPE_MAP: Record<string, string> = {
    'academia': 'Academia',
    'restaurante': 'Restaurante',
    'bar': 'Bar',
    'cafe': 'Cafeteria',
    'shopping': 'Shopping',
    'clinica': 'Clínica Médica',
    'salao': 'Salão de Beleza',
    'hotel': 'Hotel',
    'loja': 'Loja de Roupas',
    'escritorio': 'Coworking',
    'outro': 'Outro'
};

// Mapeamento de status do banco para status do componente
const STATUS_MAP: Record<string, 'active' | 'pending' | 'suspended'> = {
    'active': 'active',
    'approved': 'active',
    'pending': 'pending',
    'rejected': 'suspended',
    'inactive': 'suspended'
};

const CONTRACT_STATUS_LABELS = {
    active: { label: 'Ativo', color: 'bg-green-100 text-green-800' },
    pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' },
    suspended: { label: 'Suspenso', color: 'bg-red-100 text-red-800' }
};

// Função para mapear dados do banco para a interface do componente
const mapDBToPartner = (dbPartner: VenuePartnerDB): PointHubPartner => {
    return {
        id: dbPartner.id,
        userId: dbPartner.user_id, // ID do usuário para buscar as telas
        // Dados do Proprietário
        ownerName: dbPartner.owner_name || dbPartner.venue_name,
        document: dbPartner.owner_document || '',
        email: dbPartner.owner_email || '',
        phone: dbPartner.owner_phone || '',
        pixKey: dbPartner.pix_key || '',
        contractStatus: STATUS_MAP[dbPartner.status] || 'pending',
        // Dados do Estabelecimento
        venueName: dbPartner.venue_name,
        category: VENUE_TYPE_MAP[dbPartner.venue_type] || dbPartner.venue_type || 'Outro',
        address: `${dbPartner.venue_street}, ${dbPartner.venue_number}${dbPartner.venue_complement ? ` - ${dbPartner.venue_complement}` : ''}`,
        city: dbPartner.venue_city,
        state: dbPartner.venue_state,
        zipCode: dbPartner.venue_cep,
        latitude: dbPartner.latitude || '',
        longitude: dbPartner.longitude || '',
        audienceProfile: dbPartner.audience_profile || (dbPartner.daily_footfall ? `Fluxo diário: ${dbPartner.daily_footfall} pessoas` : ''),
        monthlyFlow: dbPartner.daily_footfall ? String(dbPartner.daily_footfall * 30) : '',
        operatingHours: dbPartner.operating_hours || '',
        // Dados Técnicos
        screenCount: dbPartner.screen_quantity || 1,
        screenPositions: dbPartner.screen_positions || [],
        screenOrientation: (dbPartner.screen_orientation as 'vertical' | 'horizontal' | 'both') || 'vertical',
        screenResolution: dbPartner.screen_resolution || 'Full HD',
        connectionType: dbPartner.connection_type || 'Wi-Fi',
        // Match
        photos: dbPartner.venue_photos || [],
        tags: dbPartner.tags || [],
        // Slots - valores definidos pelo admin-master (sem fallback mockado)
        availableSlots: dbPartner.available_slots ?? 0,
        totalSlots: dbPartner.total_slots ?? 0,
        createdAt: dbPartner.created_at,
        // Campos adicionais do banco
        venueNeighborhood: dbPartner.venue_neighborhood,
        venueNumber: dbPartner.venue_number,
        venueComplement: dbPartner.venue_complement || undefined,
        dailyFootfall: dbPartner.daily_footfall,
        averageRetention: dbPartner.average_retention,
        blacklistedCategories: dbPartner.blacklisted_categories,
        customBlacklistBrands: dbPartner.custom_blacklist_brands,
        activeDeviceCount: 0, // Será calculado após a busca principal
        internalMediaCycle: dbPartner.internal_media_cycle || 2,
        externalMediaCycle: dbPartner.external_media_cycle || 1
    };
};

const PointHubManagementSection: React.FC = () => {
    const [partners, setPartners] = useState<PointHubPartner[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPartner, setEditingPartner] = useState<PointHubPartner | null>(null);
    const [activeStep, setActiveStep] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewingPartner, setViewingPartner] = useState<PointHubPartner | null>(null);

    // Estados para o carrossel de fotos
    const [carouselOpen, setCarouselOpen] = useState(false);
    const [carouselPhotos, setCarouselPhotos] = useState<string[]>([]);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [isZoomed, setIsZoomed] = useState(false);

    // Estados para gerenciar dispositivos/aparelhos do parceiro e slots (Etapa 4)
    const [partnerDevices, setPartnerDevices] = useState<PartnerDevice[]>([]); // Aparelhos ativos do usuário parceiro
    const [selectedDeviceId, setSelectedDeviceId] = useState<string>(''); // Dispositivo selecionado no dropdown
    const [deviceSlotConfigs, setDeviceSlotConfigs] = useState<DeviceSlotConfig[]>([]); // Configurações de slots por dispositivo
    const [loadingDevices, setLoadingDevices] = useState(false); // Loading dos dispositivos

    const { user } = useAuth();
    // TODO: Implementar verificação real de role. Por enquanto, liberado ou verificar email específico
    const isMasterAdmin = true; // user?.email?.includes('admin'); 

    // Função para atualizar a quantidade total de slots do parceiro
    // SINCRONIZA: venue_partners E venue_device_slots
    const handleUpdateTotalSlots = async (partnerId: string, newTotalSlots: number) => {
        if (!isMasterAdmin) return;

        try {
            console.log('🔄 [PointHub] Atualizando total de slots:', { partnerId, newTotalSlots });

            // Buscar dados atuais do parceiro para calcular slots ocupados
            const partner = partners.find(p => p.id === partnerId);
            if (!partner) {
                console.error('Partner not found locally');
                return;
            }

            const currentTotal = partner.totalSlots || 0;
            const currentAvailable = partner.availableSlots || 0;
            const occupiedSlots = Math.max(0, currentTotal - currentAvailable);
            const newAvailableSlots = Math.max(0, newTotalSlots - occupiedSlots);

            console.log('🧮 [PointHub] Recalculando slots:', {
                currentTotal,
                currentAvailable,
                occupiedSlots,
                newTotalSlots,
                newAvailableSlots
            });

            // 1. Atualizar venue_partners
            const { error } = await supabase
                .from('venue_partners')
                .update({
                    total_slots: newTotalSlots,
                    available_slots: newAvailableSlots
                })
                .eq('id', partnerId);

            if (error) {
                console.error('❌ [PointHub] Erro ao atualizar slots em venue_partners:', error);
                alert('Erro ao atualizar slots: ' + error.message);
                return;
            }

            // 2. SINCRONIZAR: Também atualizar venue_device_slots para todos os dispositivos do parceiro
            // Primeiro, buscar dispositivos do parceiro via user_id
            if (partner.userId) {
                // Buscar dispositivos
                const { data: devices } = await supabase
                    .from('devices')
                    .select('id')
                    .eq('user_id', partner.userId);

                if (devices && devices.length > 0) {
                    // Calcular slots por dispositivo (dividir igualmente)
                    const slotsPerDevice = Math.floor(newTotalSlots / devices.length);
                    const availablePerDevice = Math.floor(newAvailableSlots / devices.length);

                    // Atualizar ou inserir para cada dispositivo
                    for (const device of devices) {
                        const { data: existingSlot } = await supabase
                            .from('venue_device_slots')
                            .select('id')
                            .eq('venue_partner_id', partnerId)
                            .eq('device_id', device.id)
                            .single();

                        if (existingSlot) {
                            await supabase
                                .from('venue_device_slots')
                                .update({
                                    slots_per_device: slotsPerDevice,
                                    available_slots: availablePerDevice
                                })
                                .eq('id', existingSlot.id);
                        } else {
                            await supabase
                                .from('venue_device_slots')
                                .insert({
                                    venue_partner_id: partnerId,
                                    device_id: device.id,
                                    slots_per_device: slotsPerDevice,
                                    available_slots: availablePerDevice
                                });
                        }
                    }
                    console.log('✅ [PointHub] venue_device_slots sincronizado!');
                }
            }

            // Atualizar estado local
            setPartners(prev => prev.map(p => {
                if (p.id === partnerId) {
                    return {
                        ...p,
                        totalSlots: newTotalSlots,
                        availableSlots: newAvailableSlots
                    };
                }
                return p;
            }));

            console.log('✅ [PointHub] Slots atualizados com sucesso!');
        } catch (err) {
            console.error('❌ [PointHub] Erro inesperado ao atualizar slots:', err);
        }
    };

    const handleUpdateMediaCycle = async (partnerId: string, field: 'internal_media_cycle' | 'external_media_cycle', value: number) => {
        if (!isMasterAdmin) return;

        try {
            const { error } = await supabase
                .from('venue_partners')
                .update({ [field]: value })
                .eq('id', partnerId);

            if (error) throw error;

            // Atualizar estado local
            setPartners(prev => prev.map(p => {
                if (p.id === partnerId) {
                    return {
                        ...p,
                        internalMediaCycle: field === 'internal_media_cycle' ? value : p.internalMediaCycle,
                        externalMediaCycle: field === 'external_media_cycle' ? value : p.externalMediaCycle
                    };
                }
                return p;
            }));

            console.log(`✅ [PointHub] ${field} atualizado para ${value}`);
        } catch (err) {
            console.error(`❌ [PointHub] Erro ao atualizar ${field}:`, err);
        }
    };

    // Função para buscar parceiros do Supabase
    const fetchPartners = async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('🔍 [PointHub] Iniciando busca de parceiros venue...');

            const { data, error: fetchError } = await supabase
                .from('venue_partners')
                .select('*')
                .order('created_at', { ascending: false });

            console.log('🔍 [PointHub] Resultado da busca:', { data, fetchError });
            console.log('🔍 [PointHub] Quantidade de registros:', data?.length || 0);

            if (fetchError) {
                console.error('❌ [PointHub] Erro ao buscar parceiros:', fetchError);
                setError(`Erro ao carregar parceiros: ${fetchError.message || 'Erro desconhecido'}.`);
                return;
            }

            if (data) {
                console.log('✅ [PointHub] Dados brutos do Supabase:', data);
                const mappedPartners = (data as VenuePartnerDB[]).map(mapDBToPartner);

                // Resolver User IDs faltantes via RPC get_profiles_with_email
                const missingUserIds = mappedPartners.filter(p => !p.userId && p.email);
                if (missingUserIds.length > 0) {
                    try {
                        const emails = missingUserIds.map(p => p.email);
                        console.log('🔍 [PointHub] Buscando perfis para emails via RPC:', emails);

                        // Usar RPC que faz join com auth.users para obter email
                        const { data: profilesWithEmail, error: rpcError } = await supabase
                            .rpc('get_profiles_with_email');

                        if (rpcError) {
                            console.warn('⚠️ [PointHub] RPC get_profiles_with_email falhou:', rpcError);
                        } else if (profilesWithEmail) {
                            console.log('📊 [PointHub] Perfis com email encontrados:', profilesWithEmail.length);
                            for (const profile of profilesWithEmail) {
                                const partner = mappedPartners.find(p =>
                                    p.email && profile.email &&
                                    p.email.toLowerCase() === profile.email.toLowerCase()
                                );
                                if (partner && !partner.userId) {
                                    console.log(`🔗 [PointHub] Vinculando user ${profile.id} ao parceiro ${partner.venueName}`);
                                    partner.userId = profile.id;

                                    // Atualizar no banco assincronamente
                                    supabase
                                        .from('venue_partners')
                                        .update({ user_id: profile.id })
                                        .eq('id', partner.id)
                                        .then(({ error }) => {
                                            if (error) console.error('❌ [PointHub] Erro ao vincular usuário no banco:', error);
                                            else console.log('✅ [PointHub] Vínculo salvo no banco para', partner.venueName);
                                        });
                                }
                            }
                        }
                    } catch (resolveError) {
                        console.error('❌ [PointHub] Erro ao resolver user IDs:', resolveError);
                    }
                }

                // Buscar contagem real de dispositivos ativos para cada parceiro
                // Primeiro, coletar todos os user_ids
                const userIds = mappedPartners.map(p => p.userId).filter(Boolean) as string[];
                console.log('🔍 [PointHub] User IDs para buscar dispositivos:', userIds);

                if (userIds.length > 0) {
                    try {
                        // Buscar dispositivos ativos por user_id
                        const { data: devicesData, error: devicesError } = await supabase
                            .from('devices')
                            .select('id, user_id, organization_id, status')
                            .in('user_id', userIds);

                        console.log('📊 [PointHub] Dispositivos encontrados por user_id:', {
                            count: devicesData?.length || 0,
                            error: devicesError
                        });

                        if (!devicesError && devicesData) {
                            const deviceCounts: Record<string, number> = {};
                            devicesData.forEach((d: any) => {
                                if (d.user_id && d.status === 'active') {
                                    deviceCounts[d.user_id] = (deviceCounts[d.user_id] || 0) + 1;
                                }
                            });
                            console.log('📊 [PointHub] Contagem de dispositivos ativos:', deviceCounts);

                            // Atualizar parceiros com a contagem real
                            mappedPartners.forEach(p => {
                                if (p.userId) {
                                    p.activeDeviceCount = deviceCounts[p.userId] || 0;
                                }
                            });
                        }

                        // Fallback: também buscar por organization_id se algum parceiro ainda tem 0 dispositivos
                        const partnersWithZeroDevices = mappedPartners.filter(p => p.userId && (p.activeDeviceCount === 0 || p.activeDeviceCount === undefined));
                        if (partnersWithZeroDevices.length > 0) {
                            console.log('🔄 [PointHub] Tentando busca alternativa por organization para', partnersWithZeroDevices.length, 'parceiros');

                            for (const partner of partnersWithZeroDevices) {
                                if (partner.userId) {
                                    // Buscar organização do usuário
                                    const { data: orgData } = await supabase
                                        .from('organizations_users')
                                        .select('organization_id')
                                        .eq('user_id', partner.userId)
                                        .limit(1);

                                    if (orgData && orgData[0]?.organization_id) {
                                        const { data: orgDevices } = await supabase
                                            .from('devices')
                                            .select('id, status')
                                            .eq('organization_id', orgData[0].organization_id);

                                        if (orgDevices) {
                                            const activeCount = orgDevices.filter((d: any) => d.status === 'active').length;
                                            console.log(`📊 [PointHub] ${partner.venueName}: ${activeCount} dispositivos ativos via organização`);
                                            partner.activeDeviceCount = activeCount;
                                        }
                                    }
                                }
                            }
                        }
                    } catch (countError) {
                        console.error('❌ [PointHub] Erro ao contar dispositivos:', countError);
                    }
                }

                console.log('✅ [PointHub] Parceiros finais:', mappedPartners.map(p => ({
                    name: p.venueName,
                    userId: p.userId,
                    email: p.email,
                    activeDevices: p.activeDeviceCount
                })));
                setPartners(mappedPartners);
            } else {
                console.log('⚠️ [PointHub] Nenhum dado retornado (data é null/undefined)');
                setPartners([]);
            }
        } catch (err) {
            console.error('❌ [PointHub] Erro inesperado:', err);
            setError('Ocorreu um erro inesperado. Verifique o console para mais detalhes.');
        } finally {
            setLoading(false);
        }
    };

    // Carregar parceiros ao montar o componente
    useEffect(() => {
        fetchPartners();
    }, []);

    // Função para buscar os dispositivos/aparelhos de um usuário parceiro
    const fetchPartnerDevices = useCallback(async (userId: string | null, partnerId?: string) => {
        if (!userId) {
            console.log('⚠️ [PointHub] Parceiro sem userId - não é possível buscar dispositivos');
            setPartnerDevices([]);
            setDeviceSlotConfigs([]);
            return;
        }

        // Usar o partnerId passado como parâmetro, ou o editingPartner.id como fallback
        const currentPartnerId = partnerId || editingPartner?.id;
        console.log('🔍 [PointHub] Partner ID para buscar slots:', currentPartnerId);

        try {
            setLoadingDevices(true);
            console.log('🖥️ [PointHub] Buscando dispositivos do usuário parceiro:', userId);

            let foundDevices: any[] = [];

            // Primeiro: Tentar buscar dispositivos diretamente pelo user_id
            const { data: devicesByUser, error: userDevicesError } = await supabase
                .from('devices')
                .select('id, name, code, status, screen_id, organization_id, user_id')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });

            console.log('📊 [PointHub] Dispositivos por user_id:', {
                count: devicesByUser?.length || 0,
                error: userDevicesError
            });

            if (!userDevicesError && devicesByUser && devicesByUser.length > 0) {
                foundDevices = devicesByUser;
            } else {
                // Segundo: Tentar buscar pela organização do usuário
                console.log('🔄 [PointHub] Buscando via organização...');

                const { data: orgMap, error: orgMapError } = await supabase
                    .from('organizations_users')
                    .select('organization_id')
                    .eq('user_id', userId)
                    .limit(1);

                console.log('📊 [PointHub] Organização do parceiro:', { orgMap, error: orgMapError });

                if (orgMap && orgMap[0]?.organization_id) {
                    const { data: devicesByOrg, error: orgDevicesError } = await supabase
                        .from('devices')
                        .select('id, name, code, status, screen_id, organization_id, user_id')
                        .eq('organization_id', orgMap[0].organization_id)
                        .order('created_at', { ascending: false });

                    console.log('📊 [PointHub] Dispositivos por organização:', {
                        count: devicesByOrg?.length || 0,
                        error: orgDevicesError
                    });

                    if (!orgDevicesError && devicesByOrg && devicesByOrg.length > 0) {
                        foundDevices = devicesByOrg;
                    }
                }
            }

            // Processar dispositivos encontrados
            if (foundDevices.length > 0) {
                console.log('✅ [PointHub] Total dispositivos encontrados:', foundDevices.length);

                const mappedDevices: PartnerDevice[] = foundDevices.map(device => ({
                    id: device.id,
                    name: device.name,
                    code: device.code,
                    status: device.status || 'inactive',
                    screen_id: device.screen_id,
                    organization_id: device.organization_id
                }));

                setPartnerDevices(mappedDevices);

                // Buscar configurações de slots já salvas no banco (venue_device_slots)
                let savedSlots: Record<string, { slots_per_device: number; available_slots: number }> = {};

                if (currentPartnerId) {
                    console.log('🔍 [PointHub] Buscando slots para partnerId:', currentPartnerId);
                    const { data: slotsData, error: slotsError } = await supabase
                        .from('venue_device_slots')
                        .select('device_id, slots_per_device, available_slots')
                        .eq('venue_partner_id', currentPartnerId);

                    if (!slotsError && slotsData) {
                        console.log('📊 [PointHub] Slots salvos encontrados:', slotsData);
                        slotsData.forEach(slot => {
                            savedSlots[slot.device_id] = {
                                slots_per_device: slot.slots_per_device,
                                available_slots: slot.available_slots
                            };
                        });
                    } else if (slotsError) {
                        console.warn('⚠️ [PointHub] Erro ao buscar slots salvos:', slotsError);
                    }
                } else {
                    console.warn('⚠️ [PointHub] Nenhum partnerId disponível para buscar slots');
                }

                // Criar configurações com valores do banco ou defaults (0 se não configurado)
                const initialConfigs: DeviceSlotConfig[] = mappedDevices.map(device => {
                    const savedConfig = savedSlots[device.id];
                    return {
                        device_id: device.id,
                        device_name: device.name,
                        device_code: device.code,
                        slots_per_device: savedConfig?.slots_per_device ?? 0, // 0 se não configurado
                        available_slots: savedConfig?.available_slots ?? 0 // 0 se não configurado
                    };
                });
                setDeviceSlotConfigs(initialConfigs);

                // Selecionar o primeiro dispositivo por padrão
                setSelectedDeviceId(mappedDevices[0].id);
            } else {
                console.log('⚠️ [PointHub] Nenhum dispositivo encontrado para o usuário');
                setPartnerDevices([]);
                setDeviceSlotConfigs([]);
                setSelectedDeviceId('');
            }
        } catch (err) {
            console.error('❌ [PointHub] Erro inesperado ao buscar dispositivos:', err);
            setPartnerDevices([]);
        } finally {
            setLoadingDevices(false);
        }
    }, [editingPartner?.id]);

    // Função para atualizar slots por dispositivo específico e salvar no banco
    // SINCRONIZA: venue_device_slots E venue_partners
    const updateDeviceSlots = async (deviceId: string, slotsPerDevice: number) => {
        // Atualizar estado local primeiro
        const updatedConfigs = deviceSlotConfigs.map(config => {
            if (config.device_id === deviceId) {
                return {
                    ...config,
                    slots_per_device: slotsPerDevice,
                    available_slots: slotsPerDevice - (config.slots_per_device - config.available_slots)
                };
            }
            return config;
        });
        setDeviceSlotConfigs(updatedConfigs);

        // Salvar no banco de dados (venue_device_slots)
        if (editingPartner?.id) {
            try {
                console.log('💾 [PointHub] Salvando slots no banco:', { deviceId, slotsPerDevice, partnerId: editingPartner.id });

                // Verificar se já existe configuração para este dispositivo
                const { data: existingConfig, error: checkError } = await supabase
                    .from('venue_device_slots')
                    .select('id, available_slots')
                    .eq('venue_partner_id', editingPartner.id)
                    .eq('device_id', deviceId)
                    .single();

                if (checkError && checkError.code !== 'PGRST116') {
                    console.error('❌ [PointHub] Erro ao verificar configuração existente:', checkError);
                }

                let newAvailableSlots = slotsPerDevice;

                if (existingConfig) {
                    // Atualizar configuração existente
                    const occupiedSlots = existingConfig.available_slots !== null
                        ? (deviceSlotConfigs.find(c => c.device_id === deviceId)?.slots_per_device || 0) - existingConfig.available_slots
                        : 0;
                    newAvailableSlots = Math.max(0, slotsPerDevice - occupiedSlots);

                    const { error: updateError } = await supabase
                        .from('venue_device_slots')
                        .update({
                            slots_per_device: slotsPerDevice,
                            available_slots: newAvailableSlots
                        })
                        .eq('id', existingConfig.id);

                    if (updateError) {
                        console.error('❌ [PointHub] Erro ao atualizar slots:', updateError);
                    } else {
                        console.log('✅ [PointHub] Slots atualizados com sucesso em venue_device_slots!');
                    }
                } else {
                    // Inserir nova configuração
                    const { error: insertError } = await supabase
                        .from('venue_device_slots')
                        .insert({
                            venue_partner_id: editingPartner.id,
                            device_id: deviceId,
                            slots_per_device: slotsPerDevice,
                            available_slots: slotsPerDevice
                        });

                    if (insertError) {
                        console.error('❌ [PointHub] Erro ao inserir slots:', insertError);
                    } else {
                        console.log('✅ [PointHub] Slots inseridos com sucesso em venue_device_slots!');
                    }
                }

                // 2. SINCRONIZAR: Também atualizar venue_partners com a soma de todos os dispositivos
                const totalSlots = updatedConfigs.reduce((sum, c) => sum + c.slots_per_device, 0);
                const totalAvailable = updatedConfigs.reduce((sum, c) => sum + c.available_slots, 0);

                console.log('🔄 [PointHub] Sincronizando venue_partners:', { totalSlots, totalAvailable });

                const { error: venueError } = await supabase
                    .from('venue_partners')
                    .update({
                        total_slots: totalSlots,
                        available_slots: totalAvailable
                    })
                    .eq('id', editingPartner.id);

                if (venueError) {
                    console.error('❌ [PointHub] Erro ao sincronizar venue_partners:', venueError);
                } else {
                    console.log('✅ [PointHub] venue_partners sincronizado!');
                    // Atualizar estado local dos partners
                    setPartners(prev => prev.map(p => {
                        if (p.id === editingPartner.id) {
                            return { ...p, totalSlots, availableSlots: totalAvailable };
                        }
                        return p;
                    }));
                }
            } catch (err) {
                console.error('❌ [PointHub] Erro inesperado ao salvar slots:', err);
            }
        }
    };

    // Calcular totais de slots (soma de todos os dispositivos)
    const totalSlotsAllDevices = deviceSlotConfigs.reduce((sum, config) => sum + config.slots_per_device, 0);
    const availableSlotsAllDevices = deviceSlotConfigs.reduce((sum, config) => sum + config.available_slots, 0);

    // Configuração do dispositivo selecionado
    const selectedDeviceConfig = deviceSlotConfigs.find(config => config.device_id === selectedDeviceId);

    const handleViewDetails = (partner: PointHubPartner) => {
        setViewingPartner(partner);
    };

    const openCarousel = (photos: string[], startIndex: number = 0) => {
        setCarouselPhotos(photos);
        setCurrentPhotoIndex(startIndex);
        setIsZoomed(false);
        setCarouselOpen(true);
    };

    const closeCarousel = () => {
        setCarouselOpen(false);
        setIsZoomed(false);
    };

    const nextPhoto = () => {
        setCurrentPhotoIndex((prev) => (prev + 1) % carouselPhotos.length);
        setIsZoomed(false);
    };

    const prevPhoto = () => {
        setCurrentPhotoIndex((prev) => (prev - 1 + carouselPhotos.length) % carouselPhotos.length);
        setIsZoomed(false);
    };

    // Form state
    const [formData, setFormData] = useState({
        ownerName: '', document: '', email: '', phone: '', pixKey: '',
        contractStatus: 'pending' as 'active' | 'pending' | 'suspended',
        venueName: '', category: '', address: '', city: '', state: '', zipCode: '',
        latitude: '', longitude: '', audienceProfile: '', monthlyFlow: '', operatingHours: '',
        screenCount: 1, screenPositions: '', screenOrientation: 'vertical' as 'vertical' | 'horizontal' | 'both',
        screenResolution: 'Full HD', connectionType: 'Wi-Fi',
        tags: '',
        slotsPerScreen: 0, // Slots por tela (definido pelo admin-master) - 0 se não configurado
        availableSlots: 0, // Slots disponíveis (editável pelo admin) - 0 se não configurado
        totalSlots: 0, // Total de slots (calculado: telas × slots por tela) - 0 se não configurado
        photos: [] as string[],
        internalMediaCycle: 2,
        externalMediaCycle: 1
    });

    const resetForm = () => {
        setFormData({
            ownerName: '', document: '', email: '', phone: '', pixKey: '',
            contractStatus: 'pending', venueName: '', category: '', address: '',
            city: '', state: '', zipCode: '', latitude: '', longitude: '',
            audienceProfile: '', monthlyFlow: '', operatingHours: '', screenCount: 1,
            screenPositions: '', screenOrientation: 'vertical', screenResolution: 'Full HD',
            connectionType: 'Wi-Fi', tags: '',
            slotsPerScreen: 0, availableSlots: 0, totalSlots: 0,
            photos: [],
            internalMediaCycle: 2, externalMediaCycle: 1
        });
        setActiveStep(1);
        // Limpar estados de dispositivos
        setPartnerDevices([]);
        setSelectedDeviceId('');
        setDeviceSlotConfigs([]);
    };

    const handleOpenNew = () => {
        setEditingPartner(null);
        resetForm();
        setIsModalOpen(true);
    };

    const handleEdit = (partner: PointHubPartner) => {
        setEditingPartner(partner);
        // Calcular slots por tela baseado nos dados existentes
        const slotsPerScreen = partner.screenCount > 0
            ? Math.round(partner.totalSlots / partner.screenCount)
            : 0; // 0 se não configurado
        setFormData({
            ...partner,
            screenPositions: partner.screenPositions.join(', '),
            tags: partner.tags.join(', '),
            slotsPerScreen: slotsPerScreen,
            photos: partner.photos || [],
            internalMediaCycle: partner.internalMediaCycle || 2,
            externalMediaCycle: partner.externalMediaCycle || 1
        });
        setActiveStep(1);
        setIsModalOpen(true);

        // Buscar dispositivos do usuário parceiro para configurar slots na etapa 4
        // Passa partnerId para garantir que os slots sejam buscados corretamente
        fetchPartnerDevices(partner.userId, partner.id);
    };

    const handleSave = async () => {
        try {
            // Mapear os dados do formulário para o formato do banco de dados
            const dbData = {
                // Dados do Proprietário
                owner_name: formData.ownerName,
                owner_document: formData.document,
                owner_email: formData.email,
                owner_phone: formData.phone,
                pix_key: formData.pixKey,
                // Dados do Estabelecimento
                venue_name: formData.venueName,
                venue_type: formData.category.toLowerCase(),
                venue_cep: formData.zipCode,
                venue_street: formData.address.split(',')[0]?.trim() || formData.address,
                venue_number: formData.address.split(',')[1]?.trim().split(' ')[0] || '',
                venue_complement: formData.address.split('-')[1]?.trim() || null,
                venue_neighborhood: '',
                venue_city: formData.city,
                venue_state: formData.state,
                operating_hours: formData.operatingHours,
                daily_footfall: formData.monthlyFlow ? Math.round(parseInt(formData.monthlyFlow) / 30) : null,
                audience_profile: formData.audienceProfile,
                // Dados Técnicos
                screen_quantity: formData.screenCount,
                screen_positions: formData.screenPositions.split(',').map(s => s.trim()).filter(Boolean),
                screen_orientation: formData.screenOrientation,
                screen_resolution: formData.screenResolution,
                connection_type: formData.connectionType,
                // Slots (definidos pelo admin-master)
                available_slots: formData.availableSlots,
                total_slots: formData.totalSlots,
                internal_media_cycle: formData.internalMediaCycle,
                external_media_cycle: formData.externalMediaCycle,
                // Match
                tags: formData.tags.split(',').map(s => s.trim()).filter(Boolean),
                latitude: formData.latitude || null,
                longitude: formData.longitude || null,
                // Status baseado no contractStatus
                status: formData.contractStatus === 'active' ? 'approved' :
                    formData.contractStatus === 'suspended' ? 'rejected' : 'pending',
                updated_at: new Date().toISOString(),
                venue_photos: formData.photos
            };

            console.log('💾 [PointHub] Salvando parceiro no banco:', dbData);

            if (editingPartner) {
                // Atualizar parceiro existente
                const { error } = await supabase
                    .from('venue_partners')
                    .update(dbData)
                    .eq('id', editingPartner.id);

                if (error) {
                    console.error('❌ [PointHub] Erro ao atualizar parceiro:', error);
                    alert('Erro ao atualizar parceiro: ' + error.message);
                    return;
                }

                console.log('✅ [PointHub] Parceiro atualizado com sucesso!');
            } else {
                // Inserir novo parceiro
                const { error } = await supabase
                    .from('venue_partners')
                    .insert([dbData]);

                if (error) {
                    console.error('❌ [PointHub] Erro ao cadastrar parceiro:', error);
                    alert('Erro ao cadastrar parceiro: ' + error.message);
                    return;
                }

                console.log('✅ [PointHub] Novo parceiro cadastrado com sucesso!');
            }

            // Recarregar a lista de parceiros do banco
            await fetchPartners();

            setIsModalOpen(false);
            resetForm();
        } catch (err) {
            console.error('❌ [PointHub] Erro inesperado ao salvar:', err);
            alert('Ocorreu um erro inesperado ao salvar.');
        }
    };

    // Estados para modal de confirmação de exclusão
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [partnerToDelete, setPartnerToDelete] = useState<PointHubPartner | null>(null);
    const [deleting, setDeleting] = useState(false);

    const handleDeleteClick = (partner: PointHubPartner) => {
        setPartnerToDelete(partner);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!partnerToDelete) return;

        try {
            setDeleting(true);

            const { error: deleteError } = await supabase
                .from('venue_partners')
                .delete()
                .eq('id', partnerToDelete.id);

            if (deleteError) {
                console.error('Erro ao deletar parceiro:', deleteError);
                alert('Erro ao excluir parceiro. Por favor, tente novamente.');
                return;
            }

            // Atualiza o estado local após sucesso
            setPartners(prev => prev.filter(p => p.id !== partnerToDelete.id));
            setDeleteModalOpen(false);
            setPartnerToDelete(null);
        } catch (err) {
            console.error('Erro inesperado:', err);
            alert('Ocorreu um erro inesperado.');
        } finally {
            setDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
        setPartnerToDelete(null);
    };

    // Estado para controlar loading do toggle de cada parceiro
    const [togglingPartnerId, setTogglingPartnerId] = useState<string | null>(null);

    // Função para alternar o status do parceiro (active/pending) para receber mídias externas
    const togglePartnerActiveStatus = async (partner: PointHubPartner) => {
        const newStatus = partner.contractStatus === 'active' ? 'pending' : 'active';

        try {
            setTogglingPartnerId(partner.id);
            console.log('🔄 [PointHub] Alternando status do parceiro:', {
                id: partner.id,
                from: partner.contractStatus,
                to: newStatus,
                partnerId: partner.id
            });

            const { data, error: updateError, count } = await supabase
                .from('venue_partners')
                .update({ status: newStatus })
                .eq('id', partner.id)
                .select();

            console.log('📊 [PointHub] Resultado da atualização:', { data, error: updateError, count });

            if (updateError) {
                console.error('❌ [PointHub] Erro ao atualizar status:', updateError);
                console.error('❌ [PointHub] Detalhes:', {
                    code: updateError.code,
                    message: updateError.message,
                    details: updateError.details,
                    hint: updateError.hint
                });
                alert(`Erro ao atualizar status: ${updateError.message || 'Erro desconhecido'}. Verifique as políticas RLS no Supabase.`);
                return;
            }

            if (!data || data.length === 0) {
                console.error('❌ [PointHub] Nenhum registro atualizado. Possível problema de RLS.');
                alert('Nenhum registro foi atualizado. Verifique se você tem permissão para alterar este parceiro.');
                return;
            }

            // Atualiza o estado local após sucesso
            setPartners(prev => prev.map(p => {
                if (p.id === partner.id) {
                    return { ...p, contractStatus: newStatus };
                }
                return p;
            }));

            console.log('✅ [PointHub] Status atualizado com sucesso para:', newStatus, '- Registros afetados:', data.length);
        } catch (err) {
            console.error('❌ [PointHub] Erro inesperado ao alternar status:', err);
            alert('Ocorreu um erro inesperado.');
        } finally {
            setTogglingPartnerId(null);
        }
    };

    const filteredPartners = partners.filter(p =>
        p.venueName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.ownerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.city.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const steps = [
        { num: 1, label: 'Parceiro' },
        { num: 2, label: 'Estabelecimento' },
        { num: 3, label: 'Inventário' },
        { num: 4, label: 'Slots' }
    ];

    return (
        <div className="w-full bg-white rounded-lg shadow p-6 mb-8">
            <div className="mb-6">
                <p className="text-gray-600">Cadastre e gerencie os parceiros ADS Venue que disponibilizam telas para publicidade.</p>
            </div>

            {/* Header com busca e botão */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Buscar por nome, local ou cidade..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={fetchPartners}
                        className="px-3 py-2 bg-white text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-orange-500 transition-all shadow-sm flex items-center justify-center group"
                        title="Atualizar Lista"
                        disabled={loading}
                    >
                        <svg className={`w-5 h-5 ${loading ? 'animate-spin text-orange-500' : 'text-gray-400 group-hover:text-orange-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                    <button
                        onClick={handleOpenNew}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:from-orange-600 hover:to-amber-600 transition-all shadow-md whitespace-nowrap"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Novo Parceiro
                    </button>
                </div>
            </div>

            {/* Lista de parceiros */}
            <div className="space-y-4">
                {/* Estado de Loading */}
                {loading && (
                    <div className="text-center py-12">
                        <div className="animate-spin w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-gray-500">Carregando parceiros...</p>
                    </div>
                )}

                {/* Estado de Erro */}
                {error && !loading && (
                    <div className="text-center py-12 bg-red-50 rounded-lg border border-red-100">
                        <svg className="w-12 h-12 mx-auto mb-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <p className="text-red-600 font-medium mb-2">{error}</p>
                        <button
                            onClick={fetchPartners}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        >
                            Tentar novamente
                        </button>
                    </div>
                )}

                {/* Lista vazia */}
                {!loading && !error && filteredPartners.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        <svg className="w-16 h-16 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-1 4h1" />
                        </svg>
                        <p className="font-medium">Nenhum parceiro cadastrado</p>
                        <p className="text-sm mt-1">Clique em "Novo Parceiro" para começar</p>
                    </div>
                )}

                {/* Lista de parceiros */}
                {!loading && !error && filteredPartners.length > 0 && (
                    filteredPartners.map(partner => (
                        <div key={partner.id} className="border rounded-xl p-4 hover:shadow-md transition-shadow bg-gradient-to-r from-white to-orange-50/30">
                            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                {/* Info principal */}
                                <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div>
                                        <h3 className="font-bold text-lg text-gray-900">{partner.venueName}</h3>
                                        <p className="text-sm text-gray-500">{partner.ownerName}</p>
                                        <span className="inline-block mt-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">{partner.category}</span>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase">Localização</p>
                                        <p className="font-medium text-sm">{partner.city}, {partner.state}</p>
                                        <p className="text-xs text-gray-500">{partner.address}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase">Inventário</p>
                                        <div className="flex items-center gap-1">
                                            <p className="font-medium text-lg text-blue-600">{partner.activeDeviceCount !== undefined ? partner.activeDeviceCount : 0}</p>
                                            <p className="text-sm text-gray-500">aparelhos ativos</p>
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-gray-400">Slots por tela</span>
                                                <input
                                                    type="number"
                                                    className="w-16 h-7 text-sm border rounded px-1 text-center focus:ring-2 focus:ring-orange-500 outline-none"
                                                    defaultValue={Math.max(0, Math.round(partner.totalSlots / (partner.screenCount || 1)))}
                                                    onBlur={(e) => {
                                                        const val = parseInt(e.target.value);
                                                        if (!isNaN(val)) {
                                                            const currentSlotsPerScreen = Math.round(partner.totalSlots / (partner.screenCount || 1));
                                                            if (val !== currentSlotsPerScreen) {
                                                                // Calcula o novo total (Slots por tela * Quantidade de telas)
                                                                const newTotal = val * (partner.screenCount || 1);
                                                                handleUpdateTotalSlots(partner.id, newTotal);
                                                            }
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.currentTarget.blur();
                                                        }
                                                    }}
                                                    disabled={!isMasterAdmin}
                                                    title={isMasterAdmin ? "Editar quantidade de slots por tela" : "Apenas admin master pode editar"}
                                                />
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-gray-400">Disp. Total</span>
                                                <span className="text-sm font-medium text-gray-600 h-7 flex items-center">{partner.availableSlots}</span>
                                            </div>
                                        </div>

                                        {/* Configuração de ciclos de mídia */}
                                        <div className="flex items-center gap-4 mt-2 pt-2 border-t border-gray-200">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-gray-400 font-medium">Ciclo Interno</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="w-16 h-7 text-sm border rounded px-1 text-center focus:ring-2 focus:ring-orange-500 outline-none"
                                                    defaultValue={partner.internalMediaCycle}
                                                    onBlur={(e) => {
                                                        const val = parseInt(e.target.value) || 1;
                                                        if (val !== partner.internalMediaCycle) {
                                                            handleUpdateMediaCycle(partner.id, 'internal_media_cycle', val);
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.currentTarget.blur();
                                                        }
                                                    }}
                                                    disabled={!isMasterAdmin}
                                                    title="Mídias internas a exibir antes de um anúncio externo"
                                                />
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-gray-400 font-medium">Ciclo Externo</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="w-16 h-7 text-sm border rounded px-1 text-center focus:ring-2 focus:ring-orange-500 outline-none"
                                                    defaultValue={partner.externalMediaCycle}
                                                    onBlur={(e) => {
                                                        const val = parseInt(e.target.value) || 1;
                                                        if (val !== partner.externalMediaCycle) {
                                                            handleUpdateMediaCycle(partner.id, 'external_media_cycle', val);
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.currentTarget.blur();
                                                        }
                                                    }}
                                                    disabled={!isMasterAdmin}
                                                    title="Anúncios externos a exibir a cada ciclo"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {/* Toggle para ativar/desativar mídias externas */}
                                        <div className="flex items-center gap-2 mb-1">
                                            <button
                                                onClick={() => togglePartnerActiveStatus(partner)}
                                                disabled={togglingPartnerId === partner.id}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 ${partner.contractStatus === 'active'
                                                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 focus:ring-green-500'
                                                    : 'bg-gray-300 focus:ring-gray-400'
                                                    } ${togglingPartnerId === partner.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                title={partner.contractStatus === 'active' ? 'Clique para desativar' : 'Clique para ativar'}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform duration-200 ease-in-out ${partner.contractStatus === 'active' ? 'translate-x-6' : 'translate-x-1'
                                                        }`}
                                                />
                                                {togglingPartnerId === partner.id && (
                                                    <span className="absolute inset-0 flex items-center justify-center">
                                                        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    </span>
                                                )}
                                            </button>
                                            <span className={`text-xs font-medium ${partner.contractStatus === 'active' ? 'text-green-600' : 'text-gray-500'}`}>
                                                {partner.contractStatus === 'active' ? 'Recebendo ADS' : 'ADS Inativo'}
                                            </span>
                                        </div>
                                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium w-fit ${CONTRACT_STATUS_LABELS[partner.contractStatus].color}`}>
                                            {CONTRACT_STATUS_LABELS[partner.contractStatus].label}
                                        </span>
                                        <div className="flex gap-2">
                                            <button onClick={() => handleViewDetails(partner)} className="text-orange-600 hover:text-orange-800 text-sm flex items-center gap-1">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                                Ver
                                            </button>
                                            <button onClick={() => handleEdit(partner)} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                                Editar
                                            </button>
                                            <button onClick={() => handleDeleteClick(partner)} className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                Excluir
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Tags */}
                            <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t">
                                {partner.tags.map((tag, i) => (
                                    <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">{tag}</span>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Modal de cadastro/edição */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
                        {/* Header do modal */}
                        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-4 text-white">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold">{editingPartner ? 'Editar Parceiro' : 'Novo Parceiro Point Hub'}</h3>
                                <button onClick={() => setIsModalOpen(false)} className="p-1 hover:bg-white/20 rounded">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            {/* Steps */}
                            <div className="flex items-center justify-center gap-2 mt-4">
                                {steps.map((step, i) => (
                                    <React.Fragment key={step.num}>
                                        <button
                                            onClick={() => setActiveStep(step.num)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${activeStep === step.num ? 'bg-white text-orange-600 font-bold' : 'bg-white/20 hover:bg-white/30'}`}
                                        >
                                            <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-xs">{step.num}</span>
                                            <span className="hidden sm:inline">{step.label}</span>
                                        </button>
                                        {i < steps.length - 1 && <div className="w-4 h-0.5 bg-white/30" />}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>

                        {/* Conteúdo do formulário */}
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                            {/* Step 1: Dados do Parceiro */}
                            {activeStep === 1 && (
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                                        <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        Dados do Parceiro (Owner)
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Nome/Razão Social *</label>
                                            <input type="text" value={formData.ownerName} onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" required />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">CNPJ/CPF *</label>
                                            <input type="text" value={formData.document} onChange={(e) => setFormData({ ...formData, document: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" required />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">E-mail *</label>
                                            <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" required />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Telefone/WhatsApp *</label>
                                            <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" required />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Chave Pix</label>
                                            <input type="text" value={formData.pixKey} onChange={(e) => setFormData({ ...formData, pixKey: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Status do Contrato</label>
                                            <select value={formData.contractStatus} onChange={(e) => setFormData({ ...formData, contractStatus: e.target.value as any })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500">
                                                <option value="pending">Pendente</option>
                                                <option value="active">Ativo</option>
                                                <option value="suspended">Suspenso</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Dados do Estabelecimento */}
                            {activeStep === 2 && (
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                                        <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-1 4h1" />
                                        </svg>
                                        Dados do Estabelecimento (Venue)
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Nome Fantasia *</label>
                                            <input type="text" value={formData.venueName} onChange={(e) => setFormData({ ...formData, venueName: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" required />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Categoria *</label>
                                            <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" required>
                                                <option value="">Selecione...</option>
                                                {VENUE_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                            </select>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium mb-1">Endereço *</label>
                                            <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" required />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Cidade *</label>
                                            <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" required />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Estado *</label>
                                            <input type="text" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" maxLength={2} required />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">CEP</label>
                                            <input type="text" value={formData.zipCode} onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Horário de Funcionamento</label>
                                            <input type="text" value={formData.operatingHours} onChange={(e) => setFormData({ ...formData, operatingHours: e.target.value })} placeholder="Ex: 06:00 - 22:00" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Fluxo Médio Mensal</label>
                                            <input type="text" value={formData.monthlyFlow} onChange={(e) => setFormData({ ...formData, monthlyFlow: e.target.value })} placeholder="Ex: 3500 pessoas" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Perfil do Público</label>
                                            <input type="text" value={formData.audienceProfile} onChange={(e) => setFormData({ ...formData, audienceProfile: e.target.value })} placeholder="Ex: Classe A/B, 25-45 anos" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Inventário */}
                            {activeStep === 3 && (
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                                        <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        Dados Técnicos do Ponto (Inventory)
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Quantidade de Telas *</label>
                                            <input type="number" min="1" value={formData.screenCount} onChange={(e) => setFormData({ ...formData, screenCount: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" required />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Orientação das Telas</label>
                                            <select value={formData.screenOrientation} onChange={(e) => setFormData({ ...formData, screenOrientation: e.target.value as any })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500">
                                                <option value="vertical">Vertical</option>
                                                <option value="horizontal">Horizontal</option>
                                                <option value="both">Ambos</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Resolução</label>
                                            <select value={formData.screenResolution} onChange={(e) => setFormData({ ...formData, screenResolution: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500">
                                                <option value="Full HD">Full HD (1080p)</option>
                                                <option value="4K">4K (2160p)</option>
                                                <option value="HD">HD (720p)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Tipo de Conexão</label>
                                            <select value={formData.connectionType} onChange={(e) => setFormData({ ...formData, connectionType: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500">
                                                <option value="Wi-Fi">Wi-Fi</option>
                                                <option value="Ethernet">Ethernet</option>
                                                <option value="4G">4G</option>
                                                <option value="5G">5G</option>
                                            </select>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium mb-1">Posicionamento das Telas</label>
                                            <input type="text" value={formData.screenPositions} onChange={(e) => setFormData({ ...formData, screenPositions: e.target.value })} placeholder="Ex: Recepção, Área de Musculação, Vestiário" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                                            <p className="text-xs text-gray-500 mt-1">Separe as posições por vírgula</p>
                                        </div>

                                    </div>
                                </div>
                            )}

                            {/* Step 4: Configuração de Slots (Admin-Master) */}
                            {activeStep === 4 && (
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                                        <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                        </svg>
                                        Configuração de Slots (Admin)
                                    </h4>

                                    {/* Configuração de Ciclos de Mídia (Global) */}
                                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <h5 className="font-medium text-orange-800">Ciclos de Exibição de Mídia (Global)</h5>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1 text-gray-700">Ciclo Interno</label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={formData.internalMediaCycle}
                                                        onChange={(e) => setFormData({ ...formData, internalMediaCycle: parseInt(e.target.value) || 1 })}
                                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                                                    />
                                                    <span className="text-xs text-gray-500 whitespace-nowrap hidden sm:inline">mídias internas</span>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">Quantidade de mídias internas a exibir antes de intercalar com externas.</p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1 text-gray-700">Ciclo Externo</label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={formData.externalMediaCycle}
                                                        onChange={(e) => setFormData({ ...formData, externalMediaCycle: parseInt(e.target.value) || 1 })}
                                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                                                    />
                                                    <span className="text-xs text-gray-500 whitespace-nowrap hidden sm:inline">anúncios externos</span>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">Quantidade de anúncios externos a exibir a cada intervalo.</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dropdown de Seleção de Dispositivo/Aparelho do Parceiro */}
                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                            <span className="font-medium text-blue-800">Selecione um Aparelho/Dispositivo do Parceiro</span>
                                        </div>

                                        {loadingDevices ? (
                                            <div className="flex items-center justify-center py-4">
                                                <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mr-2"></div>
                                                <span className="text-gray-500 text-sm">Carregando aparelhos...</span>
                                            </div>
                                        ) : partnerDevices.length > 0 ? (
                                            <select
                                                value={selectedDeviceId}
                                                onChange={(e) => setSelectedDeviceId(e.target.value)}
                                                className="w-full px-4 py-3 border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800 font-medium"
                                            >
                                                {partnerDevices.map((device, index) => (
                                                    <option key={device.id} value={device.id}>
                                                        Tela {String(index + 1).padStart(2, '0')} - {device.name} | Código: {device.code} {device.status === 'active' ? '✓' : '○'}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : editingPartner?.userId ? (
                                            <div className="text-center py-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                                <svg className="w-8 h-8 mx-auto text-yellow-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                <p className="text-yellow-700 text-sm font-medium">Nenhum aparelho/dispositivo encontrado</p>
                                                <p className="text-yellow-600 text-xs mt-1">O parceiro ainda não ativou dispositivos no dashboard</p>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
                                                <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                <p className="text-gray-500 text-sm font-medium">Parceiro sem conta vinculada</p>
                                                <p className="text-gray-400 text-xs mt-1">Não é possível buscar dispositivos</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Dados do Dispositivo Selecionado */}
                                    {selectedDeviceConfig && (
                                        <div className="bg-white border-2 border-orange-200 rounded-xl p-4 shadow-sm">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-amber-500 rounded-lg flex items-center justify-center">
                                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500 uppercase">Configurando Aparelho</p>
                                                        <p className="font-bold text-gray-800">{selectedDeviceConfig.device_name}</p>
                                                        <p className="text-xs text-blue-600 font-mono">Código: {selectedDeviceConfig.device_code}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Slots por Dispositivo (Editável) */}
                                            <div className="mb-4">
                                                <label className="block text-sm font-medium mb-1 text-gray-700">
                                                    Slots Publicitários Externos *
                                                    <span className="text-xs text-orange-500 ml-2">(Editável)</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="50"
                                                    value={selectedDeviceConfig.slots_per_device}
                                                    onChange={(e) => {
                                                        const newSlots = parseInt(e.target.value) || 1;
                                                        updateDeviceSlots(selectedDeviceId, Math.min(Math.max(newSlots, 1), 50));
                                                    }}
                                                    className="w-full px-4 py-3 border-2 border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-xl font-bold text-orange-600 text-center"
                                                />
                                                <p className="text-xs text-gray-500 mt-1 text-center">Quantidade de espaços para anúncios externos neste aparelho (1-50)</p>
                                            </div>

                                            {/* Barra visual de slots do dispositivo */}
                                            <div className="bg-gray-100 rounded-lg p-3">
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className="text-gray-600">Ocupação deste aparelho</span>
                                                    <span className="font-medium text-gray-700">
                                                        {selectedDeviceConfig.slots_per_device - selectedDeviceConfig.available_slots}/{selectedDeviceConfig.slots_per_device} slots ocupados
                                                    </span>
                                                </div>
                                                <div className="w-full bg-gray-300 rounded-full h-2">
                                                    <div
                                                        className="bg-gradient-to-r from-orange-500 to-amber-500 h-2 rounded-full transition-all duration-300"
                                                        style={{ width: `${selectedDeviceConfig.slots_per_device > 0 ? ((selectedDeviceConfig.slots_per_device - selectedDeviceConfig.available_slots) / selectedDeviceConfig.slots_per_device) * 100 : 0}%` }}
                                                    />
                                                </div>
                                                <p className="text-xs text-gray-500 mt-2 text-center">Esta barra só será preenchida quando mídias externas forem alocadas para este dispositivo</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Resumo Consolidado de Todos os Dispositivos */}
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mt-4">
                                        <h5 className="font-semibold text-green-800 flex items-center gap-2 mb-4">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                            Resumo Total do Estabelecimento
                                        </h5>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {/* Total de Aparelhos Ativos */}
                                            <div className="bg-white rounded-lg p-4 border border-blue-100">
                                                <p className="text-xs text-gray-500 uppercase mb-1">Aparelhos Ativos</p>
                                                <p className="text-2xl font-bold text-blue-600">{partnerDevices.filter(d => d.status === 'active').length}</p>
                                                <p className="text-xs text-gray-400">de {partnerDevices.length} dispositivo(s)</p>
                                            </div>

                                            {/* Total de Slots (Soma de todos os dispositivos) */}
                                            <div className="bg-white rounded-lg p-4 border border-orange-100">
                                                <p className="text-xs text-gray-500 uppercase mb-1">Total de Slots</p>
                                                <p className="text-2xl font-bold text-orange-600">{totalSlotsAllDevices}</p>
                                                <p className="text-xs text-gray-400">soma de todos os aparelhos</p>
                                            </div>

                                            {/* Slots Disponíveis */}
                                            <div className="bg-white rounded-lg p-4 border border-green-100">
                                                <p className="text-xs text-gray-500 uppercase mb-1">Slots Disponíveis</p>
                                                <p className="text-2xl font-bold text-green-600">
                                                    {availableSlotsAllDevices}
                                                    <span className="text-sm font-normal text-gray-400 ml-1">/ {totalSlotsAllDevices}</span>
                                                </p>
                                                <p className="text-xs text-gray-400">prontos para anúncios externos</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tags e Fotos */}
                                    <div className="grid grid-cols-1 gap-4 mt-6 pt-4 border-t">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Tags de Interesse</label>
                                            <input type="text" value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} placeholder="Ex: #Fitness, #Saúde, #Luxo, #Família" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                                            <p className="text-xs text-gray-500 mt-1">Separe as tags por vírgula</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Fotos do Local</label>

                                            {/* Galeria de fotos */}
                                            {formData.photos && formData.photos.length > 0 && (
                                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                                                    {formData.photos.map((photo, index) => (
                                                        <div key={index} className="relative aspect-video rounded-lg overflow-hidden border border-gray-200 group">
                                                            <img
                                                                src={photo}
                                                                alt={`Foto ${index + 1}`}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-400 transition-colors cursor-pointer">
                                                <svg className="w-10 h-10 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                <p className="text-sm text-gray-500">Clique para adicionar fotos</p>
                                                <p className="text-xs text-gray-400 mt-1">Recomendado: 2-3 fotos do local e das telas</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer do modal */}
                        <div className="border-t p-4 bg-gray-50 flex justify-between">
                            <button
                                onClick={() => activeStep > 1 ? setActiveStep(activeStep - 1) : setIsModalOpen(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                {activeStep === 1 ? 'Cancelar' : 'Voltar'}
                            </button>
                            <button
                                onClick={() => activeStep < 4 ? setActiveStep(activeStep + 1) : handleSave()}
                                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:from-orange-600 hover:to-amber-600 transition-all shadow-md"
                            >
                                {activeStep < 4 ? 'Próximo' : (editingPartner ? 'Salvar Alterações' : 'Cadastrar Parceiro')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de visualização de detalhes */}
            {viewingPartner && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl">
                        {/* Header do modal */}
                        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-bold">{viewingPartner.venueName}</h3>
                                    <p className="text-white/80 text-sm">{viewingPartner.ownerName}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${viewingPartner.contractStatus === 'active' ? 'bg-green-100 text-green-800' :
                                        viewingPartner.contractStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                        }`}>
                                        {CONTRACT_STATUS_LABELS[viewingPartner.contractStatus].label}
                                    </span>
                                    <button onClick={() => setViewingPartner(null)} className="p-1 hover:bg-white/20 rounded">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Conteúdo */}
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                            {/* Seção 1: Dados do Parceiro */}
                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3 pb-2 border-b">
                                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    Dados do Parceiro (Owner)
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">Nome/Razão Social</p>
                                        <p className="font-medium text-gray-900">{viewingPartner.ownerName}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">CNPJ/CPF</p>
                                        <p className="font-medium text-gray-900">{viewingPartner.document}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">E-mail</p>
                                        <p className="font-medium text-gray-900">{viewingPartner.email}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">Telefone/WhatsApp</p>
                                        <p className="font-medium text-gray-900">{viewingPartner.phone}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">Chave Pix</p>
                                        <p className="font-medium text-gray-900">{viewingPartner.pixKey || '-'}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">Cadastrado em</p>
                                        <p className="font-medium text-gray-900">{new Date(viewingPartner.createdAt).toLocaleDateString('pt-BR')}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Seção 2: Dados do Estabelecimento */}
                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3 pb-2 border-b">
                                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-1 4h1" />
                                    </svg>
                                    Dados do Estabelecimento (Venue)
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">Nome Fantasia</p>
                                        <p className="font-medium text-gray-900">{viewingPartner.venueName}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">Categoria</p>
                                        <p className="font-medium text-gray-900">
                                            <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-700 text-sm rounded-full">{viewingPartner.category}</span>
                                        </p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">Horário de Funcionamento</p>
                                        <p className="font-medium text-gray-900">{viewingPartner.operatingHours || '-'}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg md:col-span-2">
                                        <p className="text-xs text-gray-500 uppercase">Endereço</p>
                                        <p className="font-medium text-gray-900">{viewingPartner.address}</p>
                                        <p className="text-sm text-gray-600">{viewingPartner.city}, {viewingPartner.state} - CEP: {viewingPartner.zipCode || '-'}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">Fluxo Médio Mensal</p>
                                        <p className="font-medium text-gray-900">{viewingPartner.monthlyFlow ? `${viewingPartner.monthlyFlow} pessoas` : '-'}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg md:col-span-3">
                                        <p className="text-xs text-gray-500 uppercase">Perfil do Público</p>
                                        <p className="font-medium text-gray-900">{viewingPartner.audienceProfile || '-'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Seção 3: Inventário Técnico */}
                            <div className="mb-6">
                                <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3 pb-2 border-b">
                                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Dados Técnicos do Ponto (Inventory)
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-100">
                                        <p className="text-xs text-gray-500 uppercase">Quantidade de Telas</p>
                                        <p className="text-2xl font-bold text-orange-600">{viewingPartner.screenCount}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">Orientação</p>
                                        <p className="font-medium text-gray-900 capitalize">{viewingPartner.screenOrientation}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">Resolução</p>
                                        <p className="font-medium text-gray-900">{viewingPartner.screenResolution}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase">Conexão</p>
                                        <p className="font-medium text-gray-900">{viewingPartner.connectionType}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg md:col-span-4">
                                        <p className="text-xs text-gray-500 uppercase mb-2">Posicionamento das Telas</p>
                                        <div className="flex flex-wrap gap-2">
                                            {viewingPartner.screenPositions.length > 0 ? viewingPartner.screenPositions.map((pos, i) => (
                                                <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">{pos}</span>
                                            )) : <span className="text-gray-500">-</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Seção 4: Dados para Match */}
                            <div>
                                <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3 pb-2 border-b">
                                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                    </svg>
                                    Dados para Match do Anunciante
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg border border-green-100">
                                        <p className="text-xs text-gray-500 uppercase">Slots Disponíveis</p>
                                        <p className="text-2xl font-bold text-green-600">
                                            {viewingPartner.availableSlots} <span className="text-sm font-normal text-gray-500">/ {viewingPartner.totalSlots}</span>
                                        </p>
                                        <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all"
                                                style={{ width: `${viewingPartner.totalSlots > 0 ? (viewingPartner.availableSlots / viewingPartner.totalSlots) * 100 : 0}%` }}
                                            />
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <p className="text-xs text-gray-500 uppercase mb-2">Tags de Interesse</p>
                                        <div className="flex flex-wrap gap-2">
                                            {viewingPartner.tags.length > 0 ? viewingPartner.tags.map((tag, i) => (
                                                <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">{tag}</span>
                                            )) : <span className="text-gray-500">-</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Seção 5: Fotos do Local */}
                            <div className="mt-6">
                                <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3 pb-2 border-b">
                                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    Fotos do Local
                                </h4>
                                {viewingPartner.photos.length > 0 ? (
                                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                        {viewingPartner.photos.map((photo, index) => (
                                            <div
                                                key={index}
                                                onClick={() => openCarousel(viewingPartner.photos, index)}
                                                className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group shadow-md hover:shadow-xl transition-all duration-300"
                                            >
                                                <img
                                                    src={photo}
                                                    alt={`Foto ${index + 1} do local`}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                                />
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                                                    <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                    </svg>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                        <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <p className="text-gray-500">Nenhuma foto cadastrada</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer do modal */}
                        <div className="border-t p-4 bg-gray-50 flex justify-between">
                            <button
                                onClick={() => setViewingPartner(null)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                Fechar
                            </button>
                            <button
                                onClick={() => {
                                    handleEdit(viewingPartner);
                                    setViewingPartner(null);
                                }}
                                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Editar Parceiro
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Carrossel de Fotos */}
            {carouselOpen && carouselPhotos.length > 0 && (
                <div
                    className="fixed inset-0 bg-black/95 flex items-center justify-center z-[60]"
                    onClick={closeCarousel}
                >
                    <div className="relative w-full h-full flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
                        {/* Botão Fechar */}
                        <button
                            onClick={closeCarousel}
                            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Contador de fotos */}
                        <div className="absolute top-4 left-4 px-3 py-1 bg-white/10 rounded-full text-white text-sm">
                            {currentPhotoIndex + 1} / {carouselPhotos.length}
                        </div>

                        {/* Botão Anterior */}
                        {carouselPhotos.length > 1 && (
                            <button
                                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                                className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                        )}

                        {/* Imagem Principal */}
                        <div
                            className={`relative max-w-[90vw] max-h-[85vh] transition-transform duration-300 ${isZoomed ? 'cursor-zoom-out scale-150' : 'cursor-zoom-in'}`}
                            onClick={(e) => { e.stopPropagation(); setIsZoomed(!isZoomed); }}
                        >
                            <img
                                src={carouselPhotos[currentPhotoIndex]}
                                alt={`Foto ${currentPhotoIndex + 1}`}
                                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                                style={{ transform: isZoomed ? 'scale(1.5)' : 'scale(1)' }}
                            />
                        </div>

                        {/* Botão Próximo */}
                        {carouselPhotos.length > 1 && (
                            <button
                                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                                className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        )}

                        {/* Miniaturas */}
                        {carouselPhotos.length > 1 && (
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-black/50 rounded-lg">
                                {carouselPhotos.map((photo, index) => (
                                    <button
                                        key={index}
                                        onClick={(e) => { e.stopPropagation(); setCurrentPhotoIndex(index); setIsZoomed(false); }}
                                        className={`w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${index === currentPhotoIndex
                                            ? 'border-orange-500 ring-2 ring-orange-500/50'
                                            : 'border-transparent opacity-60 hover:opacity-100'
                                            }`}
                                    >
                                        <img
                                            src={photo}
                                            alt={`Miniatura ${index + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Instrução de Zoom */}
                        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/60 text-sm flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                            Clique na imagem para {isZoomed ? 'diminuir' : 'ampliar'}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação de Exclusão */}
            {deleteModalOpen && partnerToDelete && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
                        {/* Header com ícone de alerta */}
                        <div className="p-6 pb-4">
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">
                                        Excluir parceiro
                                    </h3>
                                    <p className="text-gray-500 mt-1">
                                        Você está prestes a remover este parceiro do sistema.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Detalhes do parceiro a ser excluído */}
                        <div className="mx-6 p-4 bg-red-50 border border-red-100 rounded-xl">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-1 4h1" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900">{partnerToDelete.venueName}</p>
                                    <p className="text-sm text-gray-600">{partnerToDelete.ownerName}</p>
                                </div>
                            </div>
                        </div>

                        {/* Aviso */}
                        <div className="px-6 py-4">
                            <p className="text-sm text-gray-500">
                                <span className="font-medium text-gray-700">Atenção:</span> Esta ação é permanente e não poderá ser desfeita.
                                Todos os dados deste parceiro serão removidos do sistema.
                            </p>
                        </div>

                        {/* Botões de ação */}
                        <div className="p-6 pt-2 flex gap-3">
                            <button
                                onClick={handleCancelDelete}
                                disabled={deleting}
                                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={deleting}
                                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {deleting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Excluindo...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Sim, excluir
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PointHubManagementSection;

